/**
 * Jump palette (mod+k, or the top bar's ⌘K button): type a few letters of a
 * token and a page ("std liq"), Enter. It lists every page (lib/palette
 * `pageEntries`), the commands components register (lib/commands) and every
 * keyboard shortcut, recent jumps first. `/` stays the search box's key.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { useJobs } from "@/api/hooks";
import { useCommandRegistry, type Command } from "@/lib/commands";
import { pageEntries, pushRecent, rank } from "@/lib/palette";
import { useShortcutRegistry, useShortcuts } from "@/lib/shortcuts";
import { loadLocal, saveLocal } from "@/lib/storage";
import { useUi } from "@/state/ui";

const PALETTE_KEY = "mod+k";

/** Mount once in the app shell. */
export function CommandPalette() {
  const open = useUi((s) => s.paletteOpen);
  const setOpen = useUi((s) => s.setPaletteOpen);
  useShortcuts([
    {
      key: PALETTE_KEY,
      description: "jump to a page or command",
      group: "navigation",
      global: true,
      handler: () => setOpen(!useUi.getState().paletteOpen),
    },
  ]);
  if (!open) return null;
  return <PaletteDialog onClose={() => setOpen(false)} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const jobs = useJobs();
  const navigate = useNavigate();
  const registered = useCommandRegistry((s) => s.commands);
  const shortcuts = useShortcutRegistry((s) => s.shortcuts);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const [recent, setRecent] = useState(() => loadLocal<string[]>("palette.recent", []));
  const input = useRef<HTMLInputElement>(null);

  // focus the input on open, and give focus back to whatever had it on close
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => previous?.focus?.();
  }, []);

  const commands = useMemo<Command[]>(() => {
    const pages = pageEntries(jobs.data?.data).map(({ route, ...e }) => ({
      ...e,
      run: () => void navigate(route),
    }));
    const keys = [...shortcuts.values()]
      .filter((s) => s.key !== PALETTE_KEY)
      .map((s) => ({
        id: `shortcut:${s.key}:${s.description}`,
        label: s.description,
        hint: s.key,
        group: s.group ?? "shortcuts",
        keywords: ["shortcut"],
        run: () => s.handler(new KeyboardEvent("keydown", { key: s.key })),
      }));
    return [...pages, ...registered.values(), ...keys];
  }, [jobs.data, navigate, registered, shortcuts]);
  const shown = useMemo(() => rank(commands, query, recent), [commands, query, recent]);
  const active = Math.min(sel, Math.max(shown.length - 1, 0));

  useEffect(() => {
    document.getElementById(`palette-opt-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const run = (c: Command) => {
    const next = pushRecent(recent, c.id);
    saveLocal("palette.recent", next);
    setRecent(next);
    onClose();
    // after the dialog has gone and focus is back, so a shortcut acts on the page
    setTimeout(c.run, 0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") setSel(Math.min(active + 1, shown.length - 1));
    else if (e.key === "ArrowUp") setSel(Math.max(active - 1, 0));
    else if (e.key === "Enter" && shown[active]) run(shown[active]);
    else if (e.key === "Escape") onClose();
    // the input is the dialog's only control: Tab stays in it
    else if (e.key !== "Tab") return;
    e.preventDefault();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[12vh]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="jump to"
        className="flex max-h-[70vh] w-[min(560px,100%)] flex-col overflow-hidden rounded border border-border-strong bg-bg-elev shadow-xl"
      >
        <input
          ref={input}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSel(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="jump to a token page, a chain, a command…"
          className="w-full border-b border-border bg-bg-elev px-3 py-2 text-[13px] text-fg outline-none"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          aria-autocomplete="list"
          aria-activedescendant={shown.length ? `palette-opt-${active}` : undefined}
          aria-label="jump to"
          autoComplete="off"
          spellCheck={false}
        />
        <ul
          id="palette-list"
          role="listbox"
          aria-label="results"
          className="min-h-0 flex-1 overflow-y-auto p-1"
        >
          {shown.map((c, i) => (
            <li
              key={c.id}
              id={`palette-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={`flex cursor-pointer items-baseline gap-2 rounded-sm px-2 py-1 text-[12px] ${i === active ? "bg-bg-hover text-fg" : "text-fg-muted"}`}
              onMouseMove={() => setSel(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => run(c)}
            >
              <span className="truncate">{c.label}</span>
              {recent.includes(c.id) && !query && <span className="text-2xs text-fg-faint">recent</span>}
              <span className="num ml-auto shrink-0 text-[11px] text-fg-faint">{c.hint ?? c.group}</span>
            </li>
          ))}
          {shown.length === 0 && (
            <li className="px-2 py-1 text-[12px] text-fg-faint">
              Nothing matches. Try a token and a page, like <span className="num">net liq</span>.
            </li>
          )}
        </ul>
        <div className="flex flex-wrap gap-3 border-t border-border px-3 py-1 text-[11px] text-fg-faint">
          <span>↑↓ move</span>
          <span>Enter open</span>
          <span>Esc close</span>
        </div>
      </div>
    </div>
  );
}
